import React, { useState } from 'react';
import { useColorTheme } from '../../context/ColorThemeContext';
import { BrandIcon } from './BrandIcons';
import { QuickContentFormV2 } from './QuickContentFormV2';
import { ArrowRight } from 'lucide-react';

const CONTENT_CATEGORIES = {
  'Social Media': [
    { id: 'facebook_ad', title: 'Facebook Ad', description: 'Targeted ad copy with audience optimization', features: ['Audience targeting', 'CTA optimization', 'A/B test ready'] },
    { id: 'instagram_ad', title: 'Instagram Post', description: 'Visual-first content with hashtag strategy', features: ['Hashtag optimization', 'Visual storytelling', 'Engagement focused'] },
    { id: 'linkedin_ad', title: 'LinkedIn Ad', description: 'Professional B2B networking content', features: ['Professional tone', 'B2B targeting', 'Lead generation'] },
    { id: 'tiktok_ad', title: 'TikTok Content', description: 'Viral-ready content for younger audiences', features: ['Trend-aware', 'Youth appeal', 'Video-first'] },
    { id: 'telegram_ad', title: 'Telegram Channel', description: 'Community messaging for engaged audiences', features: ['Community building', 'Direct messaging', 'High engagement'] },
    { id: 'whatsapp_ad', title: 'WhatsApp Message', description: 'Personal direct messaging for engagement', features: ['Personal touch', 'Direct comms', 'High open rate'] },
  ],
  'Advertising': [
    { id: 'google_ads', title: 'Google Ads', description: 'Search-optimized copy for maximum visibility', features: ['Keyword optimization', 'High conversion', 'Search intent'] },
    { id: 'image_promo', title: 'Image Promotion', description: 'AI-generated visual promotional content', features: ['AI imagery', 'Brand consistency', 'Visual impact'] },
    { id: 'video_promo', title: 'Video Promotion', description: 'Engaging video content with scripts', features: ['Video scripts', 'Visual storytelling', 'Multi-platform'] },
  ],
  'Business': [
    { id: 'product_description', title: 'Product Description', description: 'Compelling copy that converts browsers to buyers', features: ['SEO optimized', 'Conversion focused', 'Feature highlighting'] },
    { id: 'email_marketing', title: 'Email Campaign', description: 'Persuasive emails for customer engagement', features: ['Personalization', 'A/B testing', 'Open rate optimization'] },
  ],
};

// Theme utility
const useThemeClasses = () => {
  const { colorTheme } = useColorTheme();
  return {
    gradient: colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500',
    gradientLight: colorTheme === 'purple' ? 'from-purple-50/80 via-white to-pink-50/50' : colorTheme === 'blue' ? 'from-blue-50/80 via-white to-sky-50/50' : 'from-emerald-50/80 via-white to-teal-50/50',
    accent: colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-emerald-600',
    accentBg: colorTheme === 'purple' ? 'bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-600' : 'bg-emerald-600',
    ring: colorTheme === 'purple' ? 'ring-purple-500' : colorTheme === 'blue' ? 'ring-blue-500' : 'ring-emerald-500',
    dot: colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-emerald-500',
    hoverCard: colorTheme === 'purple' ? 'hover:border-purple-300 dark:hover:border-purple-600' : colorTheme === 'blue' ? 'hover:border-blue-300 dark:hover:border-blue-600' : 'hover:border-emerald-300 dark:hover:border-emerald-600',
    btnGradient: colorTheme === 'purple' ? 'from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-purple-500/20' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 shadow-blue-500/20' : 'from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 shadow-emerald-500/20',
    categoryLine: colorTheme === 'purple' ? 'from-purple-500 to-pink-500' : colorTheme === 'blue' ? 'from-blue-500 to-sky-500' : 'from-emerald-500 to-teal-500',
  };
};

const TemplateCard = ({ template, theme, onClick }) => (
  <button
    onClick={() => onClick(template)}
    className={`group text-left w-full bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-5 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${theme.hoverCard}`}
    data-testid={`template-card-${template.id}`}
  >
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-700/50 flex items-center justify-center shrink-0">
        <BrandIcon templateId={template.id} size={22} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-0.5 group-hover:translate-x-0.5 transition-transform">
          {template.title}
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mb-2.5">
          {template.description}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {template.features.map((f, i) => (
            <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400">
              {f}
            </span>
          ))}
        </div>
      </div>
      <ArrowRight className={`w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:${theme.accent} transition-colors shrink-0 mt-1`} />
    </div>
  </button>
);

export const ContentStudioV2 = ({
  onCreateContent, user, isGenerating, setIsGenerating,
  generationProgress, setGenerationProgress, onNavigate,
  setCreditPreviewData, setShowCreditPreview, setPendingGenerationData,
  sessionToken,
}) => {
  const { colorTheme } = useColorTheme();
  const theme = useThemeClasses();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showQuickForm, setShowQuickForm] = useState(false);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setShowQuickForm(true);
  };

  const handleQuickCreate = (productData) => {
    const adGenerationData = { ...productData, ad_types: [selectedTemplate.id] };
    onCreateContent(adGenerationData);
    setShowQuickForm(false);
    setSelectedTemplate(null);
  };

  // Show quick form
  if (showQuickForm && selectedTemplate) {
    return (
      <>
        <QuickContentFormV2
          template={selectedTemplate}
          onSubmit={handleQuickCreate}
          onCancel={() => { setShowQuickForm(false); setSelectedTemplate(null); }}
          isGenerating={isGenerating}
          setIsGenerating={setIsGenerating}
          generationProgress={generationProgress}
          setGenerationProgress={setGenerationProgress}
          onNavigate={onNavigate}
          sessionToken={sessionToken}
          setCreditPreviewData={setCreditPreviewData}
          setShowCreditPreview={setShowCreditPreview}
          setPendingGenerationData={setPendingGenerationData}
        />

        {/* Generating Modal */}
        {isGenerating && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl border border-slate-200/50 dark:border-slate-700/50">
              <div className="text-center">
                <div className={`animate-spin rounded-full h-12 w-12 border-4 border-slate-200 dark:border-slate-700 mx-auto mb-5 ${
                  colorTheme === 'purple' ? 'border-t-purple-600' : colorTheme === 'blue' ? 'border-t-blue-600' : 'border-t-emerald-600'
                }`} />

                <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-3">
                  Generating {selectedTemplate.title}
                </h3>

                <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
                  {generationProgress.message || 'AI is creating your content...'}
                </p>

                {generationProgress.total > 0 && (
                  <div className="mb-5">
                    <div className="flex justify-between text-xs mb-1.5 text-slate-500">
                      <span>{generationProgress.step} of {generationProgress.total}</span>
                      {generationProgress.startTime && (
                        <span className={`${colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-emerald-600'} font-medium`}>
                          {Math.floor((Date.now() - generationProgress.startTime) / 1000)}s
                        </span>
                      )}
                    </div>
                    <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-2.5">
                      <div
                        className={`bg-gradient-to-r ${theme.gradient} h-2.5 rounded-full transition-all duration-1000`}
                        style={{ width: `${generationProgress.actualProgress || Math.min(95, (generationProgress.step / generationProgress.total) * 100)}%` }}
                      />
                    </div>
                    {/* Stage dots */}
                    <div className="flex items-center justify-center gap-4 mt-3 text-[10px] text-slate-400">
                      {['initializing', ...(generationProgress.hasPersonas ? ['persona_generation'] : []), 'content_generation', 'finalizing'].map(stage => (
                        <div key={stage} className="flex items-center gap-1">
                          <div className={`w-1.5 h-1.5 rounded-full ${
                            ['initializing','persona_generation','content_generation','finalizing','complete'].indexOf(generationProgress.currentStage) >= ['initializing','persona_generation','content_generation','finalizing','complete'].indexOf(stage)
                              ? theme.dot : 'bg-slate-300 dark:bg-slate-600'
                          }`} />
                          <span className={generationProgress.currentStage === stage ? 'font-semibold text-slate-600 dark:text-slate-300' : ''}>
                            {stage === 'initializing' ? 'Init' : stage === 'persona_generation' ? 'Personas' : stage === 'content_generation' ? 'Generate' : 'Finalize'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => {
                    window.userChoseBackgroundMode = true;
                    setIsGenerating(false);
                    if (onNavigate) onNavigate('dashboard');
                  }}
                  className={`w-full bg-gradient-to-r ${theme.btnGradient} text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-lg text-sm`}
                  data-testid="go-to-dashboard-btn"
                >
                  Continue in Background
                </button>
                <p className="text-[10px] text-slate-400 mt-2">Content will keep generating. Check dashboard for status.</p>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Template selection view
  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.gradientLight} dark:from-slate-900 dark:via-slate-900 dark:to-slate-900`} data-testid="content-studio-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-10">
          <h1 className={`text-3xl font-bold bg-gradient-to-r ${theme.gradient} bg-clip-text text-transparent`}>
            Content Studio
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
            Pick a template, fill in your product details, and get professional content in seconds
          </p>
        </div>

        {/* Categories */}
        {Object.entries(CONTENT_CATEGORIES).map(([category, templates]) => (
          <div key={category} className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-base font-semibold text-slate-700 dark:text-slate-200">{category}</h2>
              <div className={`flex-1 h-px bg-gradient-to-r ${theme.categoryLine} opacity-20`} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(template => (
                <TemplateCard key={template.id} template={template} theme={theme} onClick={handleTemplateSelect} />
              ))}
            </div>
          </div>
        ))}

        {/* Bottom CTA */}
        <div className={`mt-6 p-6 rounded-2xl bg-gradient-to-r ${theme.gradient} text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">Need more customization?</h3>
              <p className="text-white/80 text-sm mt-0.5">Use the Ad Creator Hub for persona-driven, multi-type campaigns</p>
            </div>
            <button
              onClick={() => onCreateContent('advanced-form')}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm font-semibold py-2.5 px-5 rounded-xl transition-colors text-sm shrink-0"
              data-testid="advanced-creator-btn"
            >
              Advanced Creator
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentStudioV2;
